import { createEvent, createStore, sample } from 'effector'
import {
  NotificationPayload,
  showNotification,
} from '../../../features/notifications'
import { fx, Request } from '../../../libs/request'
import { WalletAlert, WalletAlertsRequest, WalletAlertsResponse } from './types'

export const alertsRequested = createEvent<WalletAlertsRequest>()
export const alertsCountsRequested = createEvent()

export const getAlertsFx = fx<void, WalletAlertsResponse>({
  method: 'GET',
  // The shared CRM base URL already includes /v1.
  url: '/wallet-guard/alerts',
})

const getPendingAlertsCountFx = fx<void, WalletAlertsResponse>({
  method: 'GET',
  url: '/wallet-guard/alerts',
  query: {
    where: JSON.stringify({ processingStatus: ['pending'] }),
    limit: '1',
  },
})

const getBlockedAlertsCountFx = fx<void, WalletAlertsResponse>({
  method: 'GET',
  url: '/wallet-guard/alerts',
  query: {
    where: JSON.stringify({ processingStatus: ['blocked'] }),
    limit: '1',
  },
})

export const $alerts = createStore<WalletAlert[]>([]).on(
  getAlertsFx.doneData,
  (_, response) => response.body.items,
)

export const $alertsTotal = createStore(0).on(
  getAlertsFx.doneData,
  (_, response) => response.body.total,
)

export const $alertsHasError = createStore(false)
  .on(getAlertsFx.fail, () => true)
  .reset(getAlertsFx)

export const $pendingAlertsCount = createStore(0).on(
  getPendingAlertsCountFx.doneData,
  (_, response) => response.body.total,
)

export const $blockedAlertsCount = createStore(0).on(
  getBlockedAlertsCountFx.doneData,
  (_, response) => response.body.total,
)

sample({
  clock: alertsRequested,
  fn: ({ page, limit, where }) => ({
    query: {
      page: String(page),
      limit: String(limit),
      where: JSON.stringify(where),
    },
  }),
  target: getAlertsFx,
})

sample({
  clock: alertsCountsRequested,
  fn: () => ({}),
  target: [getPendingAlertsCountFx, getBlockedAlertsCountFx],
})

export const alertBlockRequested = createEvent<number>()
export const alertFalsePositiveRequested = createEvent<number>()

// Both endpoints return the updated alert.
export const blockAlertFx = fx<void, WalletAlert>({ method: 'POST' })
export const markAlertFalsePositiveFx = fx<void, WalletAlert>({
  method: 'POST',
})

// The wallet-guard backend sends the error code in `message`.
const actionErrorMessages: Record<string, string> = {
  ALERT_NOT_FOUND: 'Тревога не найдена',
  ALERT_ALREADY_RESOLVED: 'Тревога уже обработана',
  ALERT_TRANSACTION_REQUIRED:
    'Для ложной тревоги нужна транзакция, у этой тревоги её нет',
}

// Which alert is in flight — the table uses it to show a spinner on that
// row and to disable the other action buttons.
export const $pendingAlertId = createStore<number | null>(null)
  .on(alertBlockRequested, (_, id) => id)
  .on(alertFalsePositiveRequested, (_, id) => id)
  .reset(blockAlertFx.finally)
  .reset(markAlertFalsePositiveFx.finally)

const $lastAlertsRequest = createStore<Request<void> | null>(null).on(
  getAlertsFx,
  (_, params) => params,
)

sample({
  clock: alertBlockRequested,
  fn: (id) => ({ url: `/wallet-guard/alerts/${id}/block` }),
  target: blockAlertFx,
})

sample({
  clock: alertFalsePositiveRequested,
  fn: (id) => ({ url: `/wallet-guard/alerts/${id}/false-positive` }),
  target: markAlertFalsePositiveFx,
})

sample({
  clock: [blockAlertFx.done, markAlertFalsePositiveFx.done],
  source: $lastAlertsRequest,
  filter: (request): request is Request<void> => Boolean(request),
  target: getAlertsFx,
})

sample({
  clock: [blockAlertFx.done, markAlertFalsePositiveFx.done],
  target: alertsCountsRequested,
})

sample({
  clock: blockAlertFx.done,
  fn: (): NotificationPayload => ({
    type: 'success',
    description: 'Кошелёк заблокирован',
  }),
  target: showNotification,
})

sample({
  clock: markAlertFalsePositiveFx.done,
  fn: (): NotificationPayload => ({
    type: 'success',
    description: 'Тревога отмечена как ложная',
  }),
  target: showNotification,
})

sample({
  clock: [blockAlertFx.failData, markAlertFalsePositiveFx.failData],
  fn: (error): NotificationPayload => ({
    type: 'error',
    description:
      actionErrorMessages[error?.body?.message] ??
      error?.body?.message ??
      'Не удалось выполнить запрос',
  }),
  target: showNotification,
})
